import { graphql, useStaticQuery } from "gatsby";
import { useMemo } from "react";

interface MarketPrice {
  amount: number;
  currencyCode: string;
}

interface MarketPriceData {
  allShopifyVariantMarketPrice: {
    nodes: ReadonlyArray<{
      variantShopifyId: string;
      marketHandle: string;
      priceAmount: number;
      priceCurrencyCode: string;
    }>;
  };
  allShopifyMarket: {
    nodes: ReadonlyArray<{
      handle: string;
      name: string;
      countryCodes: ReadonlyArray<string>;
      currencyCode: string;
    }>;
  };
}

export interface MarketPriceContext {
  /** Returns the market handle for a given country code, or undefined if not in any market */
  getMarketForCountry(countryCode: string): string | undefined;
  /** Returns the price for a variant in a given market */
  getVariantPrice(
    variantShopifyId: string,
    marketHandle: string,
  ): MarketPrice | undefined;
  /** Returns true if the country belongs to any configured market */
  isMarketCountry(countryCode: string): boolean;
}

export function useMarketPrices(): MarketPriceContext {
  const data = useStaticQuery<MarketPriceData>(graphql`
    {
      allShopifyVariantMarketPrice {
        nodes {
          variantShopifyId
          marketHandle
          priceAmount
          priceCurrencyCode
        }
      }
      allShopifyMarket {
        nodes {
          handle
          name
          countryCodes
          currencyCode
        }
      }
    }
  `);

  return useMemo(() => {
    // Sort markets by specificity: fewer countries = more specific = higher priority.
    // This ensures that when a country appears in multiple markets,
    // the more specific market wins (e.g. Ukraine over US+).
    const sortedMarkets = [...data.allShopifyMarket.nodes].sort(
      (a, b) => a.countryCodes.length - b.countryCodes.length,
    );

    // Build country -> market handle map (first assignment wins)
    const countryToMarket = new Map<string, string>();
    for (const market of sortedMarkets) {
      for (const code of market.countryCodes) {
        if (!countryToMarket.has(code)) {
          countryToMarket.set(code, market.handle);
        }
      }
    }

    // Build variantId -> marketHandle -> price map
    const variantPrices = new Map<string, Map<string, MarketPrice>>();
    for (const node of data.allShopifyVariantMarketPrice.nodes) {
      let marketMap = variantPrices.get(node.variantShopifyId);
      if (!marketMap) {
        marketMap = new Map();
        variantPrices.set(node.variantShopifyId, marketMap);
      }
      marketMap.set(node.marketHandle, {
        amount: node.priceAmount,
        currencyCode: node.priceCurrencyCode,
      });
    }

    return {
      getMarketForCountry(countryCode: string) {
        return countryToMarket.get(countryCode);
      },
      getVariantPrice(variantShopifyId: string, marketHandle: string) {
        return variantPrices.get(variantShopifyId)?.get(marketHandle);
      },
      isMarketCountry(countryCode: string) {
        return countryToMarket.has(countryCode);
      },
    };
  }, [data]);
}
